import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Target, Play, Pause, DollarSign, MoreVertical } from 'lucide-react';

// Platform icons mapping
const PLATFORM_ICONS = {
    meta: '📘',
    google: '🔍',
    tiktok: '🎵',
    linkedin: '💼'
};

// Objective icons mapping
const OBJECTIVE_ICONS = {
    AWARENESS: Target,
    TRAFFIC: Target,
    ENGAGEMENT: Target,
    LEADS: Target,
    CONVERSIONS: Target,
    SALES: DollarSign
};

/**
 * CampaignNode - Visual node for campaign in funnel builder
 */
const CampaignNode = memo(({ data, selected }) => {
    const {
        name = 'New Campaign',
        objective = 'AWARENESS',
        platform = 'meta',
        budget = { amount: 0, type: 'daily' },
        funnelStage = 'TOFU',
        status = 'draft',
        onEdit,
        onMenuClick
    } = data;

    const ObjectiveIcon = OBJECTIVE_ICONS[objective] || Target;
    const platformEmoji = PLATFORM_ICONS[platform] || '📢';

    const statusColors = {
        draft: 'var(--color-text-tertiary)',
        pending: 'var(--color-warning)',
        active: 'var(--color-success)',
        paused: 'var(--color-error)'
    };

    const stageColors = {
        TOFU: 'var(--color-info)',
        MOFU: 'var(--color-warning)',
        BOFU: 'var(--color-success)'
    };

    return (
        <div
            style={{
                background: 'var(--color-bg-primary)',
                border: selected
                    ? '2px solid var(--color-primary)'
                    : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                minWidth: '280px',
                boxShadow: selected ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                transition: 'all var(--transition-fast)'
            }}
        >
            {/* Source handle (top) - for retargeting connections */}
            <Handle
                type="target"
                position={Position.Top}
                id="retarget-in"
                style={{
                    background: 'var(--color-warning)',
                    width: '8px',
                    height: '8px'
                }}
            />

            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-3)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-primary-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <ObjectiveIcon size={18} style={{ color: 'var(--color-primary)' }} />
                    </div>
                    <div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Campaign
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            maxWidth: '180px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {name}
                        </div>
                    </div>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuClick?.();
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 'var(--space-1)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-text-tertiary)'
                    }}
                >
                    <MoreVertical size={16} />
                </button>
            </div>

            {/* Content */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-3)'
            }}>
                {/* Platform badge */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: '2px 8px',
                    background: 'var(--color-bg-tertiary)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)'
                }}>
                    {platformEmoji} {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </span>

                {/* Stage badge */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    background: `${stageColors[funnelStage]}20`,
                    color: stageColors[funnelStage],
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600
                }}>
                    {funnelStage}
                </span>

                {/* Status badge */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: '2px 8px',
                    background: `${statusColors[status]}20`,
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)'
                }}>
                    {status === 'active' && <Play size={10} fill={statusColors[status]} color={statusColors[status]} />}
                    {status === 'paused' && <Pause size={10} fill={statusColors[status]} color={statusColors[status]} />}
                    <span style={{ color: statusColors[status], textTransform: 'capitalize' }}>
                        {status}
                    </span>
                </span>
            </div>

            {/* Budget */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)'
            }}>
                <DollarSign size={14} style={{ color: 'var(--color-success)' }} />
                <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginLeft: 'var(--space-2)'
                }}>
                    ${budget.amount?.toLocaleString() || 0}
                </span>
                <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    marginLeft: 'var(--space-1)'
                }}>
                    /{budget.type || 'daily'}
                </span>
            </div>

            {/* Target handle (bottom) - connects to Ad Sets */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="adset-out"
                style={{
                    background: 'var(--color-primary)',
                    width: '10px',
                    height: '10px'
                }}
            />

            {/* Retargeting handle (right) - connects to other campaigns */}
            <Handle
                type="source"
                position={Position.Right}
                id="retarget-out"
                style={{
                    background: 'var(--color-warning)',
                    width: '8px',
                    height: '8px'
                }}
            />
        </div>
    );
});

CampaignNode.displayName = 'CampaignNode';

export default CampaignNode;
